import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    FaArrowLeft, FaPlus, FaEye, FaEyeSlash, FaTools, FaTrash, FaTimes,
    FaGlobe, FaLayerGroup, FaCheckCircle, FaUserGraduate, FaSearch,
    FaExclamationTriangle,
} from "react-icons/fa";
import { authFetch } from "../../utils/api/authFetch";
import { useTeacherTaxonomy } from "../../hooks/useTeacherTaxonomy";
import { useConfirm } from "../../components/useConfirm";
import {
    ContentThumb, typeMeta, SubjectPicker, ClassPills, TypePicker, TopicSelect, SubjectIcon,
    ProfileSetupNotice,
} from "../../components/marketplace";

// Unified teacher "Content" hub — the list page. A piece of content is a lesson
// (subject/class/topic split into segments) with a visibility: `students`
// (free, only linked students) or `public` (sold via the marketplace). This
// replaces the old separate Lesson Manager + Marketplace pages; the Profile page
// (price + subscribers + what-you-teach) is a sibling route.

const EMPTY_FORM = { title: "", subject: "", class: "", topic: "", type: "lesson_notes" };

// Shared top switch between the two teacher content pages.
export const ContentTabs = ({ active, navigate }) => (
    <div className="flex gap-1 bg-white/10 rounded-full p-1">
        {[["lessons", "Lessons", "/teacher/lessons"], ["marketplace", "Marketplace", "/teacher/marketplace"]].map(
            ([key, label, path]) => (
                <button
                    key={key}
                    onClick={() => navigate(path)}
                    className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition ${
                        active === key ? "bg-white text-blue-700" : "text-white/90 hover:bg-white/10"
                    }`}
                >
                    {label}
                </button>
            )
        )}
    </div>
);

const TeacherContent = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState(null);
    // A working teacher ends up with dozens of lessons across several subjects
    // and classes, and the list was a single undifferentiated scroll ordered by
    // creation date — so finding one meant reading every row.
    const [query, setQuery] = useState("");
    const [status, setStatus] = useState("all");
    const [subject, setSubject] = useState("");

    // Subject/class options come from the teacher's own profile, not the whole
    // question bank; topics stay global-per-subject (shared across classes).
    const { subjects, classes, topicsBySubject, hasSubjects, hasClasses, needsSetup } = useTeacherTaxonomy();
    const { confirm, ConfirmDialog } = useConfirm();

    const load = async () => {
        setLoading(true);
        try {
            const lRes = await authFetch("/api/lessons/mine");
            const lData = await lRes.json().catch(() => ({}));
            setItems(lData.data || []);
        } catch (err) {
            toast.error("Could not load your content");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (needsSetup) {
            toast.error("Set up the subjects and classes you teach first");
            return;
        }
        if (!form.title.trim() || !form.subject.trim() || !form.class.trim()) {
            toast.error("Title, subject and class are required");
            return;
        }
        // Topic is required too — it files the lesson's questions into the shared
        // bank and is how students find them.
        if (!form.topic.trim()) {
            toast.error("Choose a topic — pick one or type your own");
            return;
        }
        setSaving(true);
        try {
            const res = await authFetch("/api/lessons", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Couldn't create content");
            toast.success("Draft created — now add its sub-topics");
            setForm(EMPTY_FORM);
            setShowForm(false);
            // Straight to the Sub-topics tab: the Details tab is the form that
            // was just filled in here, and a lesson with no sub-topics can't be
            // published or generated from, so that's the only next step.
            navigate(`/teacher/lessons/${data.data.id}/build?tab=subtopics`);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const toggleStatus = async (item) => {
        const action = item.status === "published" ? "unpublish" : "publish";
        setBusyId(item.id);
        try {
            const res = await authFetch(`/api/lessons/${item.id}/${action}`, { method: "POST" });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || `Failed to ${action}`);
            toast.success(`Content ${action === "publish" ? "published" : "unpublished"}`);
            load();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (item) => {
        const ok = await confirm({
            title: `Delete "${item.title}"?`,
            message: "Its sub-topics, files and questions go with it. This can't be undone.",
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        setBusyId(item.id);
        try {
            const res = await authFetch(`/api/lessons/${item.id}`, { method: "DELETE" });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Failed to delete");
            toast.success("Content deleted");
            load();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusyId(null);
        }
    };

    // Filter options come from the lessons the teacher actually has, so the
    // dropdown can never offer a subject that returns an empty list.
    const subjectOptions = useMemo(
        () => [...new Set(items.map((i) => i.subject).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
        [items]
    );
    const counts = useMemo(() => ({
        all: items.length,
        draft: items.filter((i) => i.status !== "published").length,
        published: items.filter((i) => i.status === "published").length,
    }), [items]);

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return items.filter((i) => {
            if (status === "draft" && i.status === "published") return false;
            if (status === "published" && i.status !== "published") return false;
            if (subject && i.subject !== subject) return false;
            if (!needle) return true;
            return [i.title, i.subject, i.class, i.topic]
                .filter(Boolean)
                .some((f) => String(f).toLowerCase().includes(needle));
        });
    }, [items, query, status, subject]);

    const filtersOn = !!(query.trim() || subject || status !== "all");
    const clearFilters = () => { setQuery(""); setSubject(""); setStatus("all"); };

    return (
        <div className="min-h-screen bg-gray-50 font-montserrat">
            {ConfirmDialog}
            <nav className="bg-[#2148C0] shadow-md px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <button onClick={() => navigate("/teacher-dashboard")} className="text-white shrink-0"><FaArrowLeft size={18} /></button>
                    <h1 className="text-lg sm:text-xl font-bold text-white shrink-0">Lessons</h1>
                    <div className="hidden sm:block"><ContentTabs active="lessons" navigate={navigate} /></div>
                </div>
                <button onClick={() => setShowForm((s) => !s)} className="bg-white text-blue-700 px-3 sm:px-4 py-2 rounded-lg font-semibold flex items-center gap-2 text-sm shrink-0">
                    <FaPlus /> <span className="hidden sm:inline">New lesson</span>
                </button>
            </nav>
            <div className="sm:hidden bg-[#2148C0] px-4 pb-3"><ContentTabs active="lessons" navigate={navigate} /></div>

            <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-5">
                {showForm && (
                    <form onSubmit={handleCreate} className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 space-y-5">
                        <div className="flex items-center justify-between">
                            <h2 className="font-semibold text-gray-800">New lesson</h2>
                            <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                        </div>
                        {needsSetup && (
                            <ProfileSetupNotice
                                missingSubjects={!hasSubjects}
                                missingClasses={!hasClasses}
                                onSetup={() => navigate("/teacher/marketplace/profile")}
                            />
                        )}
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Title <span className="text-red-500">*</span></label>
                            <input required placeholder="e.g. Photosynthesis explained" className="border border-gray-200 rounded-xl p-2.5 w-full focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none" value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <SubjectPicker label="Subject" required allowCustom={false} subjects={subjects} value={form.subject}
                                onChange={(v) => setForm({ ...form, subject: v })} />
                            <ClassPills label="Class" required classes={classes} value={form.class}
                                onChange={(v) => setForm({ ...form, class: v })} />
                        </div>
                        <TopicSelect label="Topic" required hint="Pulled from your question bank for the chosen subject — the same topics apply across every class." subject={form.subject}
                            topicsBySubject={topicsBySubject} value={form.topic} onChange={(v) => setForm({ ...form, topic: v })} />
                        <TypePicker label="Content type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} />
                        <button disabled={saving || needsSetup} className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold disabled:opacity-50 flex items-center gap-2">
                            <FaPlus size={13} /> {saving ? "Creating…" : "Create & add sub-topics"}
                        </button>
                    </form>
                )}

                {loading && <p className="text-gray-500">Loading…</p>}

                {!loading && items.length === 0 && (
                    <div className="text-center py-16">
                        <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto"><FaLayerGroup size={22} /></div>
                        <p className="mt-3 text-gray-600 font-medium">No lessons yet</p>
                        <p className="text-sm text-gray-400">Create your first lesson — then split it into sub-topics and let AI draft the questions.</p>
                        <button onClick={() => setShowForm(true)} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold inline-flex items-center gap-2">
                            <FaPlus size={12} /> New lesson
                        </button>
                    </div>
                )}

                {!loading && items.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-sm p-3 sm:p-4 space-y-3">
                        <div className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
                            <FaSearch className="text-gray-400 shrink-0" size={13} />
                            <input value={query} onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search by title, subject, class or topic"
                                className="w-full text-sm outline-none bg-transparent" />
                            {query && (
                                <button onClick={() => setQuery("")} className="text-gray-300 hover:text-gray-500 shrink-0" aria-label="Clear search">
                                    <FaTimes size={12} />
                                </button>
                            )}
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                            {[["all", "All"], ["draft", "Drafts"], ["published", "Published"]].map(([value, label]) => (
                                <button key={value} onClick={() => setStatus(value)}
                                    className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition ${
                                        status === value
                                            ? "bg-blue-600 text-white border-blue-600"
                                            : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
                                    }`}>
                                    {label} ({counts[value]})
                                </button>
                            ))}
                            {subjectOptions.length > 1 && (
                                <select value={subject} onChange={(e) => setSubject(e.target.value)}
                                    className="border border-gray-200 rounded-full px-3 py-1.5 text-sm text-gray-600 ml-auto">
                                    <option value="">All subjects</option>
                                    {subjectOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            )}
                        </div>

                        {filtersOn && (
                            <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
                                <span>Showing {visible.length} of {items.length}</span>
                                <button onClick={clearFilters} className="font-semibold text-blue-600 hover:underline flex items-center gap-1">
                                    <FaTimes size={9} /> Clear filters
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {!loading && items.length > 0 && visible.length === 0 && (
                    <div className="text-center py-14 bg-white rounded-2xl shadow-sm">
                        <div className="w-14 h-14 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center mx-auto"><FaSearch size={18} /></div>
                        <p className="mt-3 text-gray-700 font-medium">No lessons match</p>
                        <p className="text-sm text-gray-400 mt-1">Try a different search, or clear the filters.</p>
                        <button onClick={clearFilters} className="mt-3 text-blue-600 font-semibold text-sm hover:underline">Clear filters</button>
                    </div>
                )}

                <div className="space-y-3">
                    {visible.map((item) => {
                        const published = item.status === "published";
                        // A lesson with no sub-topics can't be published (the API
                        // rejects it), so say that on the card instead of letting
                        // the teacher find out by pressing the button.
                        const needsSubtopics = (item.segmentCount || 0) === 0;
                        const busy = busyId === item.id;
                        const open = () => navigate(`/teacher/lessons/${item.id}/build`);

                        return (
                            <div key={item.id} className="bg-white rounded-xl shadow-sm p-4 hover:shadow-md transition">
                                <div className="flex gap-3 sm:gap-4">
                                    <button onClick={open} className="w-20 sm:w-28 shrink-0" aria-label={`Open ${item.title}`}>
                                        <ContentThumb compact type={item.type} thumbnailUrl={item.thumbnail_url} title={item.title} />
                                    </button>

                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <button onClick={open} className="min-w-0 text-left">
                                                <h3 className="font-semibold text-gray-800 hover:text-blue-700 truncate">{item.title}</h3>
                                            </button>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                <StatusBadge status={item.status} />
                                                {item.in_marketplace && <MarketplaceBadge />}
                                            </div>
                                        </div>

                                        <p className="text-sm text-gray-600 mt-1 flex items-center gap-1.5 flex-wrap">
                                            <SubjectIcon subject={item.subject} size={22} iconSize={11} />
                                            <span className="font-medium">{item.subject}</span>
                                            {item.class && <><span className="text-gray-300">·</span><span className="text-gray-500">{item.class}</span></>}
                                            {item.topic && <><span className="text-gray-300">·</span><span className="text-gray-500">{item.topic}</span></>}
                                        </p>

                                        <div className="flex items-center gap-3 text-xs text-gray-400 mt-1.5 flex-wrap">
                                            <span className="text-gray-500 font-medium">{typeMeta(item.type).label}</span>
                                            <span className="inline-flex items-center gap-1"><FaLayerGroup size={10} /> {item.segmentCount || 0} sub-topic{item.segmentCount === 1 ? "" : "s"}</span>
                                            {/* Reach only means something once students can see it —
                                                on a draft these are always 0 and just add noise. */}
                                            {published && (
                                                <>
                                                    <span className="inline-flex items-center gap-1"><FaEye size={10} /> {item.openedCount || 0} opened</span>
                                                    <span className="inline-flex items-center gap-1"><FaCheckCircle size={10} /> {item.completedCount || 0} completed</span>
                                                </>
                                            )}
                                        </div>

                                        {needsSubtopics && (
                                            <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1.5">
                                                <FaExclamationTriangle size={10} className="shrink-0" />
                                                Add a sub-topic before you can publish this.
                                            </p>
                                        )}

                                        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                                            <RowAction onClick={open} Icon={FaTools} label={needsSubtopics ? "Add sub-topics" : "Edit"} primary />
                                            <RowAction onClick={() => navigate(`/lessons/${item.id}?preview=1`)} Icon={FaUserGraduate} label="Preview" />
                                            <RowAction
                                                onClick={() => toggleStatus(item)}
                                                disabled={busy || (!published && needsSubtopics)}
                                                title={!published && needsSubtopics ? "Add a sub-topic first" : undefined}
                                                Icon={published ? FaEyeSlash : FaEye}
                                                label={published ? "Unpublish" : "Publish"}
                                            />
                                            <RowAction
                                                onClick={() => handleDelete(item)} disabled={busy}
                                                Icon={FaTrash} label="Delete" danger className="ml-auto"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// Row actions carry their label, not just an icon. Four unlabelled icons in a
// row meant the only way to tell Publish from Delete was to hover — on a phone,
// where there is no hover, that's a guess with a destructive option in it. The
// labels stay visible at every width (the row wraps instead of shedding them),
// and Delete is pushed to the far end away from the ones you press often.
const RowAction = ({ onClick, Icon, label, title, disabled, primary, danger, className = "" }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        title={title || label}
        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 border transition disabled:opacity-40 disabled:cursor-not-allowed ${
            danger
                ? "border-transparent text-gray-400 hover:text-red-600 hover:bg-red-50"
                : primary
                    ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                    : "border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-700"
        } ${className}`}
    >
        <Icon size={12} className="shrink-0" />
        <span>{label}</span>
    </button>
);

const StatusBadge = ({ status }) => (
    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
        {status}
    </span>
);

// Small badge shown on a lesson that's also listed on the paid marketplace.
export const MarketplaceBadge = () => (
    <span className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-amber-100 text-amber-700">
        <FaGlobe size={9} /> In marketplace
    </span>
);

export default TeacherContent;
